import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface WhatsAppMessageTemplate {
  id: string;
  name: string;
  template_name: string;
  language: string;
  category: string;
  content_type?: string;
  header_type?: string;
  header_text?: string;
  body_text: string;
  footer_text?: string;
  variables?: string[];
  example_values?: string[];
  buttons?: any[];
  provider: string;
  status: string;
  template_id?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  approved_at?: string;
  created_by?: string;
}

export function useWhatsAppMessageTemplates() {
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ['whatsapp-message-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('whatsapp_message_templates')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as WhatsAppMessageTemplate[];
    },
  });

  const createTemplate = useMutation({
    mutationFn: async (template: Partial<WhatsAppMessageTemplate>) => {
      const { data, error } = await supabase
        .from('whatsapp_message_templates')
        .insert([template as any])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-message-templates'] });
      toast.success('Template criado com sucesso');
    },
    onError: (error: any) => {
      toast.error('Erro ao criar template: ' + error.message);
    },
  });

  const updateTemplate = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<WhatsAppMessageTemplate> & { id: string }) => {
      const { data, error } = await supabase
        .from('whatsapp_message_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-message-templates'] });
      toast.success('Template atualizado com sucesso');
    },
    onError: (error: any) => {
      toast.error('Erro ao atualizar template: ' + error.message);
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('whatsapp_message_templates')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-message-templates'] });
      toast.success('Template deletado com sucesso');
    },
    onError: (error: any) => {
      toast.error('Erro ao deletar template: ' + error.message);
    },
  });

  const submitToTwilio = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.functions.invoke('whatsapp-submit-template', {
        body: { template_id: id }
      });
      
      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to submit template');
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-message-templates'] });
      toast.success('Template enviado para aprovação no Twilio!');
    },
    onError: (error: any) => {
      toast.error('Erro ao enviar template: ' + error.message);
    },
  });

  const syncTemplates = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('whatsapp-sync-templates', {
        body: { import_all: true }
      });
      
      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to sync templates');
      
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-message-templates'] });
      const imported = data.imported || 0;
      const updated = data.updated || 0;
      
      if (imported > 0 || updated > 0) {
        toast.success(`${imported} template(s) importado(s) e ${updated} atualizado(s)!`);
      } else {
        toast.info('Templates já estão atualizados');
      }
    },
    onError: (error: any) => {
      toast.error('Erro ao sincronizar: ' + error.message);
    },
  });

  return {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    submitToTwilio,
    syncTemplates,
  };
}
